import { FC, useState, useMemo } from 'react';
import {
  Grid,
  Button,
  Box,
  Typography,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
} from '@mui/material';
import PaginatedGrid from '../../../../components/paginatedGrid/PaginatedGrid';
import {
  Add,
  MenuBook,
  Science,
  Computer,
  FilterList,
  ExpandMore,
  Close,
  Storage,
  VideoLibrary,
  LibraryBooks,
  Newspaper,
  ArrowBack,
} from '@mui/icons-material';
import {
  useAcademicsLibraryContext,
  useAcademicsLibraryState,
  useAcademicsLibraryActions,
} from '../../contexts/academicsLibrary.context';
import {
  LibraryAssetType,
  BorrowingStatus,
  BorrowerType,
  IBorrowingRecord,
} from '../../../../../apis/academics/academicsLibrary.interfaces';
import { MaterialCondition } from '../../../../../apis/academics/academicsMaterials.interfaces';
import {
  GridColDef,
  GridRenderCellParams,
  GridValidRowModel,
} from '@mui/x-data-grid';
import { CURRENCY_SYMBOL } from '../../../../../constants/currency.constants';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { TTranslateTextType } from 'src/hooks/useTranslationHook';
import { ELibraryView } from './academicsLibraryView.interfaces';

interface AcademicsLibraryGridProps {
  isLoading?: boolean;
}

interface LibraryRow extends GridValidRowModel {
  id: string;
  assetCode: string;
  title: string;
  assetType: LibraryAssetType;
  currentStatus: BorrowingStatus;
  condition: MaterialCondition;
  location: string;
  author?: string;
  purchaseCost?: number;
  totalBorrows: number;
  isAvailable: boolean;
}

interface FilterState {
  assetTypes: Set<LibraryAssetType>;
  statuses: Set<BorrowingStatus>;
  conditions: Set<MaterialCondition>;
  locations: Set<string>;
}

interface BorrowingHistoryRow extends GridValidRowModel {
  id: string;
  recordId: string;
  borrowerName: string;
  borrowerType: BorrowerType;
  borrowerEmail?: string;
  borrowedDate: string;
  dueDate: string;
  returnDate?: string;
  status: BorrowingStatus;
  renewalCount: number;
  fineAmount?: number;
  finePaid?: boolean;
}

const getAssetTypeIcon = (assetType: LibraryAssetType) => {
  switch (assetType) {
    case LibraryAssetType.BOOK:
      return <MenuBook fontSize="small" />;
    case LibraryAssetType.LAB_EQUIPMENT:
      return <Science fontSize="small" />;
    case LibraryAssetType.DIGITAL_RESOURCE:
      return <Computer fontSize="small" />;
    case LibraryAssetType.STUDY_MATERIAL:
      return <LibraryBooks fontSize="small" />;
    case LibraryAssetType.AUDIO_VISUAL:
      return <VideoLibrary fontSize="small" />;
    case LibraryAssetType.MAGAZINE:
    case LibraryAssetType.NEWSPAPER:
      return <Newspaper fontSize="small" />;
    default:
      return <Storage fontSize="small" />;
  }
};

const getLibraryGridColumns = (
  translateText: TTranslateTextType
): GridColDef<LibraryRow>[] => [
    {
      field: 'assetCode',
      headerName: translateText('academics.library.columns.assetCode'),
      width: 150,
      renderCell: (params: GridRenderCellParams<LibraryRow>) => {
        const value = params.value as string;
        return (
          <Typography
            variant="body2"
            sx={{ height: '100%', display: 'flex', alignItems: 'center' }}
          >
            {!value || value === '' ? '—' : value}
          </Typography>
        );
      },
    },
    {
      field: 'title',
      headerName: translateText('academics.library.columns.title'),
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams<LibraryRow>) => {
        const value = params.value as string;
        return (
          <Typography
            variant="body2"
            sx={{ height: '100%', display: 'flex', alignItems: 'center' }}
          >
            {!value || value === '' ? '—' : value}
          </Typography>
        );
      },
    },
    {
      field: 'assetType',
      headerName: translateText('academics.library.columns.type'),
      width: 150,
      renderCell: (params: GridRenderCellParams<LibraryRow>) => {
        const assetType = params.value as LibraryAssetType;
        if (!assetType) {
          return <Typography variant="body2">—</Typography>;
        }
        return (
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}
          >
            {assetType && getAssetTypeIcon(assetType)}
            <Typography
              variant="body2"
              sx={{ height: '100%', display: 'flex', alignItems: 'center' }}
            >
              {assetType
                .replace(/_/g, ' ')
                .replace(/\b\w/g, (l) => l.toUpperCase())}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'author',
      headerName: translateText('academics.library.columns.author'),
      width: 150,
      renderCell: (params: GridRenderCellParams<LibraryRow>) => {
        const value = params.value as string | undefined;
        return (
          <Typography
            variant="body2"
            sx={{ height: '100%', display: 'flex', alignItems: 'center' }}
          >
            {!value || value === '' ? '—' : value}
          </Typography>
        );
      },
    },
    {
      field: 'currentStatus',
      headerName: translateText('academics.library.columns.status'),
      width: 120,
      renderCell: (params: GridRenderCellParams<LibraryRow>) => {
        const status = params.value as BorrowingStatus;
        if (!status) {
          return (
            <Typography
              variant="body2"
              sx={{ height: '100%', display: 'flex', alignItems: 'center' }}
            >
              —
            </Typography>
          );
        }
        return (
          <Chip
            label={status
              .replace(/_/g, ' ')
              .replace(/\b\w/g, (l) => l.toUpperCase())}
            color={
              status === BorrowingStatus.AVAILABLE
                ? 'success'
                : status === BorrowingStatus.BORROWED
                  ? 'info'
                  : status === BorrowingStatus.OVERDUE
                    ? 'error'
                    : status === BorrowingStatus.RESERVED
                      ? 'warning'
                      : 'default'
            }
            size="small"
          />
        );
      },
    },
    {
      field: 'condition',
      headerName: translateText('academics.library.columns.condition'),
      width: 120,
      renderCell: (params: GridRenderCellParams<LibraryRow>) => {
        const condition = params.value as MaterialCondition;
        if (!condition) {
          return (
            <Typography
              variant="body2"
              sx={{ height: '100%', display: 'flex', alignItems: 'center' }}
            >
              —
            </Typography>
          );
        }
        return (
          <Chip
            label={condition}
            color={
              condition === MaterialCondition.NEW
                ? 'success'
                : condition === MaterialCondition.LIKE_NEW
                  ? 'info'
                  : condition === MaterialCondition.GOOD
                    ? 'primary'
                    : condition === MaterialCondition.FAIR
                      ? 'warning'
                      : 'error'
            }
            size="small"
          />
        );
      },
    },
    {
      field: 'location',
      headerName: translateText('academics.library.columns.location'),
      width: 200,
      renderCell: (params: GridRenderCellParams<LibraryRow>) => {
        const value = params.value as string;
        return (
          <Typography
            variant="body2"
            sx={{ height: '100%', display: 'flex', alignItems: 'center' }}
          >
            {!value || value === '' ? '—' : value}
          </Typography>
        );
      },
    },
    {
      field: 'purchaseCost',
      headerName: translateText('academics.library.columns.cost'),
      width: 120,
      type: 'number',
      renderCell: (params: GridRenderCellParams<LibraryRow>) => {
        const value = params.value as number | undefined;
        if (value === undefined || value === null) {
          return (
            <Typography
              variant="body2"
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'end',
              }}
            >
              —
            </Typography>
          );
        }
        const numValue = Number(value);
        if (isNaN(numValue)) {
          return (
            <Typography
              variant="body2"
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'end',
              }}
            >
              —
            </Typography>
          );
        }
        return (
          <Typography
            variant="body2"
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'end',
            }}
          >
            {`${CURRENCY_SYMBOL}${numValue.toFixed(2)}`}
          </Typography>
        );
      },
    },
    {
      field: 'totalBorrows',
      headerName: translateText('academics.library.columns.borrows'),
      width: 100,
      type: 'number',
      renderCell: (params: GridRenderCellParams<LibraryRow>) => {
        const value = params.value as number | undefined;
        return (
          <Typography
            variant="body2"
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'end',
            }}
          >
            {value === undefined || value === null ? '—' : value}
          </Typography>
        );
      },
    },
  ];

const getBorrowingHistoryGridColumns = (
  translateText: TTranslateTextType
): GridColDef<BorrowingHistoryRow>[] => [
    {
      field: 'borrowerName',
      headerName: translateText('academics.library.columns.borrower'),
      width: 180,
      renderCell: (params: GridRenderCellParams<BorrowingHistoryRow>) => {
        const value = params.value as string;
        return (
          <Typography
            variant="body2"
            sx={{ height: '100%', display: 'flex', alignItems: 'center' }}
          >
            {!value || value === '' ? '—' : value}
          </Typography>
        );
      },
    },
    {
      field: 'borrowerType',
      headerName: translateText('academics.library.columns.type'),
      width: 120,
      renderCell: (params: GridRenderCellParams<BorrowingHistoryRow>) => {
        const borrowerType = params.value as BorrowerType;
        if (!borrowerType) {
          return (
            <Typography
              variant="body2"
              sx={{ height: '100%', display: 'flex', alignItems: 'center' }}
            >
              —
            </Typography>
          );
        }
        return (
          <Chip
            label={borrowerType
              .replace(/_/g, ' ')
              .replace(/\b\w/g, (l) => l.toUpperCase())}
            size="small"
            color="default"
          />
        );
      },
    },
    {
      field: 'borrowedDate',
      headerName: translateText('academics.library.columns.borrowedDate'),
      width: 150,
      renderCell: (params: GridRenderCellParams<BorrowingHistoryRow>) => {
        const value = params.value as string | undefined;
        if (!value) {
          return (
            <Typography
              variant="body2"
              sx={{ height: '100%', display: 'flex', alignItems: 'center' }}
            >
              —
            </Typography>
          );
        }
        try {
          const date = new Date(value);
          if (isNaN(date.getTime())) {
            return (
              <Typography
                variant="body2"
                sx={{ height: '100%', display: 'flex', alignItems: 'center' }}
              >
                —
              </Typography>
            );
          }
          return (
            <Typography
              variant="body2"
              sx={{ height: '100%', display: 'flex', alignItems: 'center' }}
            >
              {date.toLocaleDateString()}
            </Typography>
          );
        } catch {
          return (
            <Typography
              variant="body2"
              sx={{ height: '100%', display: 'flex', alignItems: 'center' }}
            >
              —
            </Typography>
          );
        }
      },
    },
    {
      field: 'dueDate',
      headerName: translateText('academics.library.columns.dueDate'),
      width: 150,
      renderCell: (params: GridRenderCellParams<BorrowingHistoryRow>) => {
        const value = params.value as string | undefined;
        if (!value) {
          return (
            <Typography
              variant="body2"
              sx={{ height: '100%', display: 'flex', alignItems: 'center' }}
            >
              —
            </Typography>
          );
        }
        try {
          const date = new Date(value);
          if (isNaN(date.getTime())) {
            return (
              <Typography
                variant="body2"
                sx={{ height: '100%', display: 'flex', alignItems: 'center' }}
              >
                —
              </Typography>
            );
          }
          return (
            <Typography
              variant="body2"
              sx={{ height: '100%', display: 'flex', alignItems: 'center' }}
            >
              {date.toLocaleDateString()}
            </Typography>
          );
        } catch {
          return (
            <Typography
              variant="body2"
              sx={{ height: '100%', display: 'flex', alignItems: 'center' }}
            >
              —
            </Typography>
          );
        }
      },
    },
    {
      field: 'returnDate',
      headerName: translateText('academics.library.columns.returnDate'),
      width: 150,
      renderCell: (params: GridRenderCellParams<BorrowingHistoryRow>) => {
        const value = params.value as string | undefined;
        if (!value) {
          return (
            <Typography
              variant="body2"
              sx={{ height: '100%', display: 'flex', alignItems: 'center' }}
            >
              —
            </Typography>
          );
        }
        try {
          const date = new Date(value);
          if (isNaN(date.getTime())) {
            return (
              <Typography
                variant="body2"
                sx={{ height: '100%', display: 'flex', alignItems: 'center' }}
              >
                —
              </Typography>
            );
          }
          return (
            <Typography
              variant="body2"
              sx={{ height: '100%', display: 'flex', alignItems: 'center' }}
            >
              {date.toLocaleDateString()}
            </Typography>
          );
        } catch {
          return (
            <Typography
              variant="body2"
              sx={{ height: '100%', display: 'flex', alignItems: 'center' }}
            >
              —
            </Typography>
          );
        }
      },
    },
    {
      field: 'status',
      headerName: translateText('academics.library.columns.status'),
      width: 120,
      renderCell: (params: GridRenderCellParams<BorrowingHistoryRow>) => {
        const status = params.value as BorrowingStatus;
        if (!status) {
          return (
            <Typography
              variant="body2"
              sx={{ height: '100%', display: 'flex', alignItems: 'center' }}
            >
              —
            </Typography>
          );
        }
        return (
          <Chip
            label={status
              .replace(/_/g, ' ')
              .replace(/\b\w/g, (l) => l.toUpperCase())}
            color={
              status === BorrowingStatus.AVAILABLE
                ? 'success'
                : status === BorrowingStatus.BORROWED
                  ? 'info'
                  : status === BorrowingStatus.OVERDUE
                    ? 'error'
                    : 'default'
            }
            size="small"
          />
        );
      },
    },
    {
      field: 'renewalCount',
      headerName: translateText('academics.library.columns.renewals'),
      width: 100,
      type: 'number',
      renderCell: (params: GridRenderCellParams<BorrowingHistoryRow>) => {
        const value = params.value as number | undefined;
        return (
          <Typography
            variant="body2"
            sx={{ height: '100%', display: 'flex', alignItems: 'center' }}
          >
            {value === undefined || value === null ? '—' : value}
          </Typography>
        );
      },
    },
    {
      field: 'fineAmount',
      headerName: translateText('academics.library.columns.fine'),
      width: 120,
      type: 'number',
      renderCell: (params: GridRenderCellParams<BorrowingHistoryRow>) => {
        const value = params.value as number | undefined;
        if (value === undefined || value === null) {
          return (
            <Typography
              variant="body2"
              sx={{ height: '100%', display: 'flex', alignItems: 'center' }}
            >
              —
            </Typography>
          );
        }
        const numValue = Number(value);
        if (isNaN(numValue)) {
          return (
            <Typography
              variant="body2"
              sx={{ height: '100%', display: 'flex', alignItems: 'center' }}
            >
              —
            </Typography>
          );
        }
        return (
          <Typography
            variant="body2"
            sx={{ height: '100%', display: 'flex', alignItems: 'center' }}
          >
            {`${CURRENCY_SYMBOL}${numValue.toFixed(2)}`}
          </Typography>
        );
      },
    },
    {
      field: 'finePaid',
      headerName: translateText('academics.library.columns.finePaid'),
      width: 120,
      renderCell: (params: GridRenderCellParams<BorrowingHistoryRow>) => {
        const finePaid = params.value as boolean | undefined;
        if (finePaid === undefined || finePaid === null) {
          return (
            <Typography
              variant="body2"
              sx={{ height: '100%', display: 'flex', alignItems: 'center' }}
            >
              —
            </Typography>
          );
        }
        return (
          <Chip
            label={
              finePaid
                ? translateText('academics.library.paid')
                : translateText('academics.library.unpaid')
            }
            color={finePaid ? 'success' : 'error'}
            size="small"
          />
        );
      },
    },
  ];

const AcademicsLibraryGrid: FC<AcademicsLibraryGridProps> = ({
  isLoading: propIsLoading,
}) => {
  const { translateText } = useTranslation();
  const {
    libraryQuery,
    getTotalLibraryValue,
    getAssetTypeSummary,
    getStatusSummary,
    getLocationSummary,
    getBorrowingHistory,
    getItemById,
  } = useAcademicsLibraryContext();
  const {
    academicsLibraryAppState: { selectedLibraryItem },
  } = useAcademicsLibraryState();
  const {
    setIsManageLibraryItemOpen,
    setSelectedLibraryItem,
    setSelectedLibraryItemId,
    setIsCreateMode,
    setIsEditMode,
    setIsManageBorrowingRecordOpen,
    setSelectedLibraryItemIdForBorrowing,
    setIsCreateBorrowingRecordMode,
    setSelectedBorrowingRecord,
  } = useAcademicsLibraryActions();

  const [viewType, setViewType] = useState<ELibraryView>(
    ELibraryView.LIBRARY_ITEMS
  );
  const [filters, setFilters] = useState<FilterState>({
    assetTypes: new Set(),
    statuses: new Set(),
    conditions: new Set(),
    locations: new Set(),
  });

  const libraryGridColumns = useMemo(
    () => getLibraryGridColumns(translateText),
    [translateText]
  );
  const borrowingHistoryGridColumns = useMemo(
    () => getBorrowingHistoryGridColumns(translateText),
    [translateText]
  );

  const isLoading = propIsLoading || libraryQuery.isLoading;

  // Transform library items into rows for the grid
  const libraryRows = useMemo(() => {
    if (!libraryQuery.data) return [];
    return libraryQuery.data.items.map((item) => ({
      id: item.id,
      assetCode: item.assetCode,
      title: item.title,
      assetType: item.assetType,
      currentStatus: item.currentStatus,
      condition: item.condition,
      location: item.location,
      author: item.author,
      purchaseCost: item.purchaseCost,
      totalBorrows: item.totalBorrows,
      isAvailable: item.isAvailable,
    }));
  }, [libraryQuery.data]);

  // Filter library items based on all active filters
  const filteredLibraryItems = useMemo(() => {
    return libraryRows.filter((row) => {
      // Asset type filter
      if (
        filters.assetTypes.size > 0 &&
        !filters.assetTypes.has(row.assetType)
      ) {
        return false;
      }

      // Status filter
      if (
        filters.statuses.size > 0 &&
        !filters.statuses.has(row.currentStatus)
      ) {
        return false;
      }

      // Condition filter
      if (
        filters.conditions.size > 0 &&
        !filters.conditions.has(row.condition)
      ) {
        return false;
      }

      // Location filter
      if (filters.locations.size > 0 && !filters.locations.has(row.location)) {
        return false;
      }

      return true;
    });
  }, [libraryRows, filters]);

  const toggleFilter = (
    filterType: keyof FilterState,
    value: LibraryAssetType | BorrowingStatus | MaterialCondition | string
  ) => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      const currentSet = new Set(prev[filterType]);

      if (currentSet.has(value)) {
        currentSet.delete(value);
      } else {
        currentSet.add(value);
      }

      return {
        ...newFilters,
        [filterType]: currentSet,
      };
    });
  };

  const clearFilters = (filterType?: keyof FilterState) => {
    if (filterType) {
      setFilters((prev) => ({
        ...prev,
        [filterType]: new Set(),
      }));
    } else {
      setFilters({
        assetTypes: new Set(),
        statuses: new Set(),
        conditions: new Set(),
        locations: new Set(),
      });
    }
  };

  const hasActiveFilters = () => {
    return Object.values(filters).some((set) => set.size > 0);
  };

  const getActiveFilterCount = (filterType: keyof FilterState) => {
    return filters[filterType].size;
  };

  const handleAddEditLibraryItem = () => {
    const isEditMode = viewType === ELibraryView.DETAILS && selectedLibraryItem;
    setIsCreateMode(!isEditMode);
    setIsEditMode(!!isEditMode);
    setSelectedLibraryItem(isEditMode ? selectedLibraryItem : null);
    setSelectedLibraryItemId(isEditMode ? selectedLibraryItem?.id || '' : null);
    setIsManageLibraryItemOpen(true);

    setIsCreateBorrowingRecordMode(false);
    setSelectedLibraryItemIdForBorrowing(null);
    setIsManageBorrowingRecordOpen(false);
  };

  const handleAddBorrowingRecord = () => {
    if (viewType === ELibraryView.DETAILS && selectedLibraryItem) {
      setIsCreateBorrowingRecordMode(true);
      setSelectedLibraryItemIdForBorrowing(selectedLibraryItem.id);
      setIsManageBorrowingRecordOpen(true);
      setSelectedBorrowingRecord(null);
    }

    setIsCreateMode(false);
    setIsEditMode(false);
    setIsManageLibraryItemOpen(false);
  };

  const handleEditBorrowingRecord = (borrowingRecord: IBorrowingRecord) => {
    if (viewType === ELibraryView.DETAILS && selectedLibraryItem) {
      setIsCreateBorrowingRecordMode(true);
      setSelectedLibraryItemIdForBorrowing(selectedLibraryItem.id);
      setIsManageBorrowingRecordOpen(true);
      setSelectedBorrowingRecord(borrowingRecord);
    }

    setIsCreateMode(false);
    setIsEditMode(false);
    setIsManageLibraryItemOpen(false);
  };

  const handleRowClick = (params: any) => {
    if (viewType === ELibraryView.LIBRARY_ITEMS) {
      const item = getItemById(params.row.id);
      if (item) {
        setSelectedLibraryItem(item);
        setSelectedLibraryItemId(item.id);
        setViewType(ELibraryView.DETAILS);
      }
    }
  };

  const handleBorrowingHistoryRowClick = (params: any) => {
    if (viewType === ELibraryView.DETAILS) {
      const record = borrowingHistory.find(
        (r) => r.recordId === params.row.recordId
      );
      if (record) {
        handleEditBorrowingRecord(record);
      }
    }
  };

  const handleBackToLibrary = () => {
    setViewType(ELibraryView.LIBRARY_ITEMS);
    setSelectedLibraryItem(null);
    setSelectedLibraryItemId(null);
  };

  const borrowingHistory = useMemo(() => {
    if (!selectedLibraryItem) return [];
    return getBorrowingHistory(selectedLibraryItem.id);
  }, [selectedLibraryItem, getBorrowingHistory]);

  const borrowingHistoryRows = useMemo(() => {
    return borrowingHistory.map((record) => ({
      id: record.recordId,
      recordId: record.recordId,
      borrowerName: record.borrowerName,
      borrowerType: record.borrowerType,
      borrowerEmail: record.borrowerEmail,
      borrowedDate: record.borrowedDate,
      dueDate: record.dueDate,
      returnDate: record.returnDate,
      status: record.status,
      renewalCount: record.renewalCount,
      fineAmount: record.fineAmount,
      finePaid: record.finePaid,
    }));
  }, [borrowingHistory]);

  const totalValue = getTotalLibraryValue();
  const assetTypeSummary = getAssetTypeSummary();
  const statusSummary = getStatusSummary();
  const locationSummary = getLocationSummary();

  return (
    <Grid
      container
      spacing={0}
      sx={{ height: '100%' }}
      data-testid="academics-library-grid"
    >
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: { xs: 2, sm: 0 },
          mb: { xs: 2, sm: 2 },
        }}
        data-testid="academics-library-header"
      >
        {viewType === ELibraryView.DETAILS && selectedLibraryItem ? (
          <Box
            display="flex"
            flexDirection={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            gap={{ xs: 1, sm: 2 }}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            <Button
              startIcon={<ArrowBack />}
              onClick={handleBackToLibrary}
              sx={{
                minHeight: { xs: '44px', sm: '36px' },
                fontSize: { xs: '16px', sm: '14px' },
              }}
              data-testid="back-to-library-button"
            >
              {translateText('academics.library.backToLibrary')}
            </Button>
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                wordBreak: 'break-word',
              }}
            >
              {selectedLibraryItem.title} -{' '}
              {translateText('academics.library.details')}
            </Typography>
          </Box>
        ) : (
          <Box>
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}
            >
              {translateText('academics.library.totalValue')}: {CURRENCY_SYMBOL}
              {totalValue.toFixed(2)} |{' '}
              {translateText('academics.library.items')}:{' '}
              {libraryQuery.data?.items.length || 0}
            </Typography>
          </Box>
        )}

        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={handleAddEditLibraryItem}
          sx={{
            minHeight: { xs: '44px', sm: '36px' },
            fontSize: { xs: '16px', sm: '14px' },
            width: { xs: '100%', sm: 'auto' },
          }}
          data-testid="add-edit-library-item-button"
        >
          {viewType === ELibraryView.LIBRARY_ITEMS
            ? translateText('academics.library.addLibraryItem')
            : translateText('academics.library.editLibraryItem')}
        </Button>
      </Grid>

      {viewType === ELibraryView.DETAILS && selectedLibraryItem && (
        <Grid item xs={12} sx={{ mb: { xs: 2, sm: 2 } }}>
          <Box
            sx={{
              p: { xs: 1, sm: 2 },
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              mb: 2,
            }}
            data-testid="library-item-details-card"
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
            >
              {translateText('academics.library.itemDetails')}
            </Typography>
            <Grid container spacing={{ xs: 1, sm: 2 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="textSecondary">
                  {translateText('academics.library.assetCode')}
                </Typography>
                <Typography variant="body1">
                  {selectedLibraryItem.assetCode}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="textSecondary">
                  {translateText('academics.library.title')}
                </Typography>
                <Typography variant="body1">
                  {selectedLibraryItem.title}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="textSecondary">
                  {translateText('academics.library.author')}
                </Typography>
                <Typography variant="body1">
                  {selectedLibraryItem.author ||
                    translateText('academics.library.nA')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="textSecondary">
                  {translateText('academics.library.assetType')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getAssetTypeIcon(selectedLibraryItem.assetType)}
                  <Typography variant="body1">
                    {selectedLibraryItem.assetType
                      .replace(/_/g, ' ')
                      .replace(/\b\w/g, (l) => l.toUpperCase())}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ display: 'block' }}
                >
                  {translateText('academics.library.status')}
                </Typography>
                <Chip
                  label={selectedLibraryItem.currentStatus
                    .replace(/_/g, ' ')
                    .replace(/\b\w/g, (l) => l.toUpperCase())}
                  color={
                    selectedLibraryItem.currentStatus ===
                      BorrowingStatus.AVAILABLE
                      ? 'success'
                      : selectedLibraryItem.currentStatus ===
                        BorrowingStatus.BORROWED
                        ? 'info'
                        : selectedLibraryItem.currentStatus ===
                          BorrowingStatus.OVERDUE
                          ? 'error'
                          : 'default'
                  }
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ display: 'block' }}
                >
                  {translateText('academics.library.condition')}
                </Typography>
                <Chip
                  label={selectedLibraryItem.condition}
                  color={
                    selectedLibraryItem.condition === MaterialCondition.NEW
                      ? 'success'
                      : selectedLibraryItem.condition ===
                        MaterialCondition.LIKE_NEW
                        ? 'info'
                        : selectedLibraryItem.condition ===
                          MaterialCondition.GOOD
                          ? 'primary'
                          : selectedLibraryItem.condition ===
                            MaterialCondition.FAIR
                            ? 'warning'
                            : 'error'
                  }
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="textSecondary">
                  {translateText('academics.library.location')}
                </Typography>
                <Typography variant="body1">
                  {selectedLibraryItem.location}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="textSecondary">
                  {translateText('academics.library.purchaseCost')}
                </Typography>
                <Typography variant="body1">
                  {selectedLibraryItem.purchaseCost
                    ? `${CURRENCY_SYMBOL}${selectedLibraryItem.purchaseCost.toFixed(2)}`
                    : translateText('academics.library.nA')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="textSecondary">
                  {translateText('academics.library.totalBorrows')}
                </Typography>
                <Typography variant="body1">
                  {selectedLibraryItem.totalBorrows}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="textSecondary">
                  {translateText('academics.library.available')}
                </Typography>
                <Chip
                  label={
                    selectedLibraryItem.isAvailable
                      ? translateText('academics.library.yes')
                      : translateText('academics.library.no')
                  }
                  color={selectedLibraryItem.isAvailable ? 'success' : 'error'}
                  size="small"
                />
              </Grid>
              {selectedLibraryItem.currentBorrowing && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                      {translateText('academics.library.currentBorrowing')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="textSecondary">
                      {translateText('academics.library.borrower')}
                    </Typography>
                    <Typography variant="body1">
                      {selectedLibraryItem.currentBorrowing.borrowerName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="textSecondary">
                      {translateText('academics.library.dueDate')}
                    </Typography>
                    <Typography variant="body1">
                      {new Date(
                        selectedLibraryItem.currentBorrowing.dueDate
                      ).toLocaleDateString()}
                    </Typography>
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
        </Grid>
      )}

      {viewType === ELibraryView.LIBRARY_ITEMS && (
        <Grid item xs={12} sx={{ mb: { xs: 2, sm: 2 } }}>
          <Accordion
            defaultExpanded={hasActiveFilters()}
            sx={{
              '&:before': {
                display: 'none',
              },
              boxShadow: 'none',
              border: '1px solid',
              borderColor: 'divider',
            }}
            data-testid="library-filters-accordion"
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{
                '& .MuiAccordionSummary-content': {
                  margin: '8px 0',
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  gap: 1,
                  width: '100%',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  <FilterList color="action" />
                  <Typography
                    variant="subtitle1"
                    sx={{
                      flexGrow: 1,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                    }}
                  >
                    {translateText('academics.library.filters')}
                  </Typography>
                </Box>
                {hasActiveFilters() && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: { xs: 1, sm: 0 },
                      width: { xs: '100%', sm: 'auto' },
                    }}
                  >
                    <Badge
                      badgeContent={Object.values(filters).reduce(
                        (acc, set) => acc + set.size,
                        0
                      )}
                      color="primary"
                      sx={{ ml: { xs: 0, sm: 1 } }}
                    >
                      <Chip
                        size="medium"
                        label={translateText('academics.library.activeFilters')}
                        color="primary"
                        variant="outlined"
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      />
                    </Badge>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<Close />}
                      onClick={(e) => {
                        e.stopPropagation();
                        clearFilters();
                      }}
                      sx={{
                        ml: { xs: 0, sm: 2 },
                        minHeight: { xs: '44px', sm: '32px' },
                        fontSize: { xs: '16px', sm: '14px' },
                        width: { xs: '100%', sm: 'auto' },
                      }}
                      data-testid="clear-all-filters-button"
                    >
                      {translateText('academics.library.clearAllFilters')}
                    </Button>
                  </Box>
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Grid container spacing={{ xs: 1, sm: 2 }}>
                <Grid item xs={12}>
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle2"
                      color="textSecondary"
                      gutterBottom
                    >
                      {translateText('academics.library.assetTypes')}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: { xs: 0.5, sm: 1 },
                        flexWrap: 'wrap',
                      }}
                    >
                      {assetTypeSummary.map((summary) => (
                        <Chip
                          key={summary.assetType}
                          label={`${summary.assetType
                            .replace(/_/g, ' ')
                            .replace(/\b\w/g, (l) =>
                              l.toUpperCase()
                            )} (${summary.totalCount})`}
                          color={
                            filters.assetTypes.has(summary.assetType)
                              ? 'primary'
                              : 'default'
                          }
                          variant={
                            filters.assetTypes.has(summary.assetType)
                              ? 'filled'
                              : 'outlined'
                          }
                          onClick={() =>
                            toggleFilter('assetTypes', summary.assetType)
                          }
                          sx={{
                            cursor: 'pointer',
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            minHeight: { xs: '32px', sm: '32px' },
                            '&:hover': {
                              backgroundColor: filters.assetTypes.has(
                                summary.assetType
                              )
                                ? 'primary.main'
                                : 'action.hover',
                            },
                          }}
                        />
                      ))}
                      {getActiveFilterCount('assetTypes') > 0 && (
                        <Chip
                          label={translateText('academics.library.clearTypes')}
                          color="error"
                          variant="outlined"
                          onClick={() => clearFilters('assetTypes')}
                          sx={{ cursor: 'pointer' }}
                        />
                      )}
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle2"
                      color="textSecondary"
                      gutterBottom
                    >
                      {translateText('academics.library.status')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {statusSummary.map((summary) => (
                        <Chip
                          key={summary.status}
                          label={`${summary.status
                            .replace(/_/g, ' ')
                            .replace(/\b\w/g, (l) =>
                              l.toUpperCase()
                            )} (${summary.totalCount})`}
                          color={
                            filters.statuses.has(summary.status)
                              ? 'primary'
                              : 'default'
                          }
                          variant={
                            filters.statuses.has(summary.status)
                              ? 'filled'
                              : 'outlined'
                          }
                          onClick={() =>
                            toggleFilter('statuses', summary.status)
                          }
                          sx={{
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: filters.statuses.has(
                                summary.status
                              )
                                ? 'primary.main'
                                : 'action.hover',
                            },
                          }}
                        />
                      ))}
                      {getActiveFilterCount('statuses') > 0 && (
                        <Chip
                          label={translateText('academics.library.clearStatus')}
                          color="error"
                          variant="outlined"
                          onClick={() => clearFilters('statuses')}
                          sx={{ cursor: 'pointer' }}
                        />
                      )}
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle2"
                      color="textSecondary"
                      gutterBottom
                    >
                      {translateText('academics.library.condition')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {Object.values(MaterialCondition).map(
                        (condition: MaterialCondition) => (
                          <Chip
                            key={condition as string}
                            label={condition}
                            color={
                              filters.conditions.has(condition)
                                ? 'primary'
                                : 'default'
                            }
                            variant={
                              filters.conditions.has(condition)
                                ? 'filled'
                                : 'outlined'
                            }
                            onClick={() =>
                              toggleFilter('conditions', condition)
                            }
                            sx={{
                              cursor: 'pointer',
                              '&:hover': {
                                backgroundColor: filters.conditions.has(
                                  condition
                                )
                                  ? 'primary.main'
                                  : 'action.hover',
                              },
                            }}
                          />
                        )
                      )}
                      {getActiveFilterCount('conditions') > 0 && (
                        <Chip
                          label={translateText(
                            'academics.library.clearCondition'
                          )}
                          color="error"
                          variant="outlined"
                          onClick={() => clearFilters('conditions')}
                          sx={{ cursor: 'pointer' }}
                        />
                      )}
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle2"
                      color="textSecondary"
                      gutterBottom
                    >
                      {translateText('academics.library.locations')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {locationSummary.map((summary) => (
                        <Chip
                          key={summary.location}
                          label={`${summary.location} (${summary.totalCount})`}
                          color={
                            filters.locations.has(summary.location)
                              ? 'primary'
                              : 'default'
                          }
                          variant={
                            filters.locations.has(summary.location)
                              ? 'filled'
                              : 'outlined'
                          }
                          onClick={() =>
                            toggleFilter('locations', summary.location)
                          }
                          sx={{
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: filters.locations.has(
                                summary.location
                              )
                                ? 'primary.main'
                                : 'action.hover',
                            },
                          }}
                        />
                      ))}
                      {getActiveFilterCount('locations') > 0 && (
                        <Chip
                          label={translateText(
                            'academics.library.clearLocations'
                          )}
                          color="error"
                          variant="outlined"
                          onClick={() => clearFilters('locations')}
                          sx={{ cursor: 'pointer' }}
                        />
                      )}
                    </Box>
                  </Box>
                </Grid>

                {hasActiveFilters() && (
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Chip
                        label={translateText(
                          'academics.library.clearAllFilters'
                        )}
                        color="error"
                        variant="outlined"
                        onClick={() => clearFilters()}
                        sx={{ cursor: 'pointer' }}
                      />
                    </Box>
                  </Grid>
                )}
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>
      )}

      {viewType === ELibraryView.DETAILS && selectedLibraryItem && (
        <Grid item xs={12} sx={{ mb: 2 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="textSecondary">
              {translateText('academics.library.showingBorrowingRecords', {
                count: borrowingHistory.length,
                plural: borrowingHistory.length !== 1 ? 's' : '',
                title: selectedLibraryItem.title,
              })}
            </Typography>
          </Box>
        </Grid>
      )}

      <Grid
        item
        xs={12}
        data-testid="academics-library-grid-container"
        sx={{
          height: 'calc(100vh - 250px)',
          overflow: 'auto',
        }}
      >
        <PaginatedGrid
          data-testid="academics-library-grid"
          rows={
            viewType === ELibraryView.LIBRARY_ITEMS
              ? filteredLibraryItems
              : borrowingHistoryRows
          }
          columns={
            viewType === ELibraryView.LIBRARY_ITEMS
              ? libraryGridColumns
              : borrowingHistoryGridColumns
          }
          loading={isLoading}
          onRowClick={
            viewType === ELibraryView.LIBRARY_ITEMS
              ? handleRowClick
              : handleBorrowingHistoryRowClick
          }
          getRowId={(row) => {
            if (!row || !row.id) {
              console.warn('Invalid row data:', row);
              return '';
            }
            return row.id;
          }}
          hideColumnsOnMobile={
            viewType === ELibraryView.LIBRARY_ITEMS
              ? ['author', 'purchaseCost', 'totalBorrows']
              : ['borrowerType', 'renewalCount', 'fineAmount', 'finePaid']
          }
        />
      </Grid>

      {viewType === ELibraryView.DETAILS ? (
        <Grid
          item
          xs={12}
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            my: { xs: 2, sm: 2 },
          }}
        >
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={handleAddBorrowingRecord}
            sx={{
              minHeight: { xs: '44px', sm: '36px' },
              fontSize: { xs: '16px', sm: '14px' },
              width: { xs: '100%', sm: 'auto' },
            }}
            data-testid="add-borrowing-record-button"
          >
            {translateText('academics.library.addBorrowingRecord')}
          </Button>
        </Grid>
      ) : null}
    </Grid>
  );
};

export default AcademicsLibraryGrid;
